import type { WindowComparison } from "../../shared/monitoring";

/** The subset of a Pulse window the delta lines read. Matches `overview.metrics` and `overview.prior`. */
export type ComparableWindow = {
  runs: number;
  successes: number;
  failures: number;
  resolvedRuns: number;
  successRate: number | null;
  costUsd: number;
  costReportedRuns: number;
};

const PREFIX = "vs prior 7 days: ";

function formatCost(value: number): string {
  return `$${value.toFixed(value < 100 ? 2 : 0)}`;
}

/** "+3 runs", "−2 runs", "+1 run", "same number of runs". Only when the windows are comparable. */
export function describeRunsDelta(comparison: WindowComparison | null | undefined): string | null {
  if (!comparison?.comparable) return null;
  const delta = comparison.runsDelta;
  if (delta === 0) return `${PREFIX}same number of runs`;
  return `${PREFIX}${delta > 0 ? "+" : "−"}${Math.abs(delta)} ${Math.abs(delta) === 1 ? "run" : "runs"}`;
}

/** "89% → 92% (19 → 24 resolved)", prior first. Only when both windows have three resolved outcomes. */
export function describeReliabilityChange(
  current: ComparableWindow | null | undefined,
  prior: ComparableWindow | null | undefined,
  comparison: WindowComparison | null | undefined,
): string | null {
  if (!comparison?.comparable || !current || !prior || current.successRate == null || prior.successRate == null) return null;
  return `${PREFIX}${Math.round(prior.successRate * 100)}% → ${Math.round(current.successRate * 100)}% (${prior.resolvedRuns} → ${current.resolvedRuns} resolved)`;
}

/** "$4.10 → $6.23", prior first. Only when comparable and both windows had runs that reported cost. */
export function describeCostChange(
  current: ComparableWindow | null | undefined,
  prior: ComparableWindow | null | undefined,
  comparison: WindowComparison | null | undefined,
): string | null {
  if (!comparison?.comparable || !current || !prior || current.costReportedRuns === 0 || prior.costReportedRuns === 0) return null;
  return `${PREFIX}${formatCost(prior.costUsd)} → ${formatCost(current.costUsd)}`;
}
